import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { DetailFieldComponent } from '../detail-field/detail-field.component';
import {
  IDetailField,
  IDetailFieldChange,
} from '../detail-field/detail-field.models';

/**
 * A block of detail rows: section header, optional header actions, and the
 * fields themselves.
 *
 * Purely presentational — the host owns the data *and* the visibility/order
 * preferences, because a detail panel configures all of its blocks from one
 * shared drawer (`ui-detail-fields-settings`) rather than per section.
 * `fields` is therefore already filtered and ordered when it arrives here.
 */
@Component({
  selector: 'ui-detail-field-list',
  standalone: true,
  imports: [CommonModule, TranslateModule, DetailFieldComponent],
  templateUrl: './detail-field-list.component.html',
  styleUrls: ['./detail-field-list.component.scss'],
})
export class DetailFieldListComponent {
  /**
   * Section heading (i18n key). Deliberately not called `title`: a static
   * `title="..."` in a host template also lands on the DOM element and the
   * browser renders it as a native tooltip over the whole block.
   */
  @Input() heading = '';

  /** Visible fields, in display order. */
  @Input() fields: IDetailField[] = [];

  /** False when the record can no longer be edited (cancelled, sent to ERP…). */
  @Input() editable = true;

  /**
   * Named templates for rows that render richer markup than a string, keyed by
   * `IDetailField.slot`.
   */
  @Input() slots: Record<string, TemplateRef<unknown>> = {};

  /**
   * Templates rendered immediately *after* a given row, keyed by
   * `IDetailField.slot`. For extras that belong to one field rather than to the
   * whole block — an AI suggestion under the company row, say, which would read
   * as unattached if it sat below the billing address and payment terms too.
   * Block-level extras still go through `<ng-content>`.
   */
  @Input() afterSlots: Record<string, TemplateRef<unknown>> = {};

  /** A `modal` row asked to open its dialog. */
  @Output() fieldEdit = new EventEmitter<IDetailField>();

  /** An `inline` row was saved. */
  @Output() fieldValueChange = new EventEmitter<IDetailFieldChange>();

  @ViewChildren(DetailFieldComponent) private rows?: QueryList<DetailFieldComponent>;

  /**
   * Opens a row's inline editor from outside — used where another surface deep
   * links into a specific field (the side-by-side quick-actions row jumps to the
   * Details tab and focuses PO number / quote id). Returns false when the field
   * is not currently visible, so callers can fall back.
   */
  startEdit(key: string): boolean {
    const row = this.rows?.find((item) => item.field?.key === key);
    if (!row) {
      return false;
    }
    row.startInlineEdit();
    return true;
  }

  onFieldValueChange(field: IDetailField, value: string | number | null): void {
    this.fieldValueChange.emit({ key: field.key, value });
  }

  slotFor(field: IDetailField): TemplateRef<unknown> | null {
    return (field.slot && this.slots[field.slot]) || null;
  }

  afterSlotFor(field: IDetailField): TemplateRef<unknown> | null {
    return (field.slot && this.afterSlots[field.slot]) || null;
  }

  trackByKey(_index: number, field: IDetailField): string {
    return field.key;
  }
}
